import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { pedir, pedirArchivo, subirArchivo } from '../api/cliente';
import { Boton } from './ui/basicos';
import { useTextoDeError } from './ui/estados';
import { Campo, Interruptor, Selector } from './ui/formulario';
import type { CampoPersonalizado, EntidadConCampos } from '../paginas/CamposPersonalizados';

/**
 * Los campos propios del comercio, dibujados según su tipo.
 *
 * ## Por qué es un componente y no está escrito en cada ficha
 *
 * Porque son los mismos campos, con las mismas reglas, en el artículo y en el cliente. Escribirlo
 * dos veces sería tener dos pantallas que aceptan cosas distintas de la misma definición.
 *
 * ## Los nueve tipos
 *
 * Están los nueve. `imagen` y `archivo` guardan en la ficha **el identificador del archivo**, no su
 * contenido, y suben apenas se elige el archivo; el detalle está abajo, en `CampoDeArchivo`.
 */
export function CamposDeLaFicha({
  entidad,
  valores,
  onCambiar,
}: {
  readonly entidad: EntidadConCampos;
  readonly valores: Readonly<Record<string, string>>;
  readonly onCambiar: (codigo: string, valor: string) => void;
}): React.ReactElement | null {
  const { t } = useTranslation();

  const campos = useQuery({
    queryKey: ['campos', entidad],
    queryFn: () => pedir<CampoPersonalizado[]>(`/campos?entidad=${entidad}`),
  });

  const activos = (campos.data ?? []).filter((campo) => campo.activo);
  // Sin campos definidos no hay sección: un título con nada abajo ocupa lugar y no dice nada.
  if (activos.length === 0) return null;

  return (
    <section className="flex flex-col gap-3 border-t border-borde-tenue pt-3">
      <h3 className="text-apoyo font-semibold uppercase tracking-wider text-texto-tenue">
        {t('campos.enLaFicha')}
      </h3>
      {activos.map((campo) => {
        const valor = valores[campo.codigo] ?? '';
        const comun = {
          etiqueta: campo.nombre,
          obligatorio: campo.obligatorio,
          ...(campo.ayuda === null ? {} : { ayuda: campo.ayuda }),
        };

        if (campo.tipo === 'si_no') {
          return (
            <Interruptor
              key={campo.id}
              etiqueta={campo.nombre}
              {...(campo.ayuda === null ? {} : { ayuda: campo.ayuda })}
              checked={valor === 'si'}
              onChange={(evento) => {
                // «si» o «no», escritos: el servidor no adivina con «true» ni con «1».
                onCambiar(campo.codigo, evento.target.checked ? 'si' : 'no');
              }}
            />
          );
        }

        if (campo.tipo === 'lista') {
          return (
            <Selector
              key={campo.id}
              {...comun}
              value={valor}
              onChange={(evento) => {
                onCambiar(campo.codigo, evento.target.value);
              }}
            >
              {/* La opción vacía existe salvo que el campo sea obligatorio: sin ella no se puede
                  dejar en blanco algo que se cargó por error. */}
              {campo.obligatorio ? null : <option value="" />}
              {campo.opciones.map((opcion) => (
                <option key={opcion} value={opcion}>
                  {opcion}
                </option>
              ))}
            </Selector>
          );
        }

        if (campo.tipo === 'texto_largo' || campo.tipo === 'texto_enriquecido') {
          return (
            <label key={campo.id} className="flex flex-col gap-1">
              <span className="text-apoyo font-medium">{campo.nombre}</span>
              <textarea
                rows={campo.tipo === 'texto_enriquecido' ? 6 : 3}
                value={valor}
                onChange={(evento) => {
                  onCambiar(campo.codigo, evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1.5 text-cuerpo"
              />
              {campo.ayuda === null ? null : (
                <span className="text-apoyo text-texto-tenue">{campo.ayuda}</span>
              )}
            </label>
          );
        }

        if (campo.tipo === 'imagen' || campo.tipo === 'archivo') {
          return (
            <CampoDeArchivo
              key={campo.id}
              campo={campo}
              valor={valor}
              onCambiar={(nuevo) => {
                onCambiar(campo.codigo, nuevo);
              }}
            />
          );
        }

        return (
          <Campo
            key={campo.id}
            {...comun}
            type={campo.tipo === 'fecha' ? 'date' : 'text'}
            inputMode={campo.tipo === 'numero' ? 'decimal' : undefined}
            value={valor}
            onChange={(evento) => {
              onCambiar(campo.codigo, evento.target.value);
            }}
          />
        );
      })}
    </section>
  );
}

/**
 * Un campo de imagen o de archivo.
 *
 * ## Qué guarda la ficha
 *
 * **El identificador del archivo, no su contenido.** El archivo se sube apenas se elige —no al
 * guardar la ficha— porque subir ocho megas mientras alguien espera el botón «Guardar» hace que la
 * pantalla parezca colgada. Cuando la subida termina, la ficha ya tiene qué guardar.
 *
 * ## Por qué la imagen se muestra y el archivo se enlaza
 *
 * Porque una foto de un artículo se mira y un PDF se abre. Mostrar el nombre de un archivo de
 * imagen obliga a abrirlo para saber si es el que corresponde.
 *
 * ## Por qué el contenido se pide con `pedirArchivo` y no se apunta con la ruta
 *
 * Porque **el token vive en memoria y viaja como encabezado**: una etiqueta `img` o un enlace los
 * pide el navegador por su cuenta, sin encabezados nuestros, y el servidor contesta 401. La primera
 * versión de esto apuntaba a `/api/archivos/…` y la imagen no se veía, sin ningún error a la vista
 * —una imagen que no carga no rompe nada—. Lo encontró el guión de humo preguntando cuánto medía la
 * imagen ya cargada.
 */
function CampoDeArchivo({
  campo,
  valor,
  onCambiar,
}: {
  readonly campo: CampoPersonalizado;
  readonly valor: string;
  readonly onCambiar: (valor: string) => void;
}): React.ReactElement {
  const { t } = useTranslation();
  const textoDeError = useTextoDeError();
  const [subiendo, setSubiendo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // **Se guarda de qué archivo es la dirección, no sólo la dirección.** Así lo que se dibuja sale
  // de comparar con el valor de ahora, y una foto vieja no queda en pantalla un instante después
  // de que la ficha cambió de archivo o se lo sacaron.
  const [traido, setTraido] = useState<{ readonly id: string; readonly direccion: string } | null>(
    null,
  );

  useEffect(() => {
    if (valor === '') return undefined;
    let vigente = true;
    let aSoltar: string | null = null;
    void pedirArchivo(`/archivos/${valor}`).then((direccion) => {
      // Si la ficha ya cambió de archivo mientras éste viajaba, se suelta acá mismo: quedarse con
      // la dirección de un contenido que nadie va a mostrar es memoria retenida sin motivo.
      if (!vigente) {
        URL.revokeObjectURL(direccion);
        return;
      }
      aSoltar = direccion;
      setTraido({ id: valor, direccion });
    });
    return () => {
      vigente = false;
      if (aSoltar !== null) URL.revokeObjectURL(aSoltar);
    };
  }, [valor]);

  const direccion = traido !== null && traido.id === valor ? traido.direccion : null;

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-apoyo font-medium">
        {campo.nombre}
        {campo.obligatorio ? ' *' : ''}
      </span>

      {direccion === null ? null : campo.tipo === 'imagen' ? (
        <img
          src={direccion}
          alt={campo.nombre}
          // `self-start` para que el marco abrace la foto: sin eso el contenedor en columna la
          // estira a todo el ancho y queda un recuadro enorme con la imagen flotando al medio.
          className="max-h-32 w-auto self-start rounded-marco border border-borde object-contain"
        />
      ) : (
        <a
          href={direccion}
          target="_blank"
          rel="noreferrer"
          className="text-cuerpo text-acento underline"
        >
          {t('campos.verElArchivo')}
        </a>
      )}

      <div className="flex items-center gap-2">
        {/* El control de archivo va escondido detrás de una etiqueta: el que trae el navegador se
            dibuja con su propio estilo y con el texto en el idioma del navegador, que en una
            pantalla en español dice «Choose file». La etiqueta hace de botón sin ningún guión. */}
        <label
          className={`cursor-pointer rounded-marco border border-borde px-2.5 py-1 text-apoyo
            ${subiendo ? 'opacity-60' : 'hover:bg-superficie-tenue'}`}
        >
          {subiendo
            ? t('campos.subiendo')
            : valor === ''
              ? t('campos.elegirArchivo')
              : t('campos.cambiarArchivo')}
          <input
            type="file"
            accept={campo.tipo === 'imagen' ? 'image/*' : undefined}
            disabled={subiendo}
            className="sr-only"
            onChange={(evento) => {
              const elegido = evento.target.files?.[0];
              if (elegido === undefined) return;
              setSubiendo(true);
              setError(null);
              subirArchivo<{ id: string }>('/archivos', elegido)
                .then((subido) => {
                  onCambiar(subido.id);
                })
                .catch((fallo: unknown) => {
                  setError(textoDeError(fallo));
                })
                .finally(() => {
                  setSubiendo(false);
                });
            }}
          />
        </label>
        {valor === '' ? null : (
          <Boton
            tono="sutil"
            onClick={() => {
              // Sacarlo de la ficha no borra el archivo del disco: el mismo archivo puede estar en
              // otra ficha, y borrarlo dejaría a la otra sin explicación.
              onCambiar('');
            }}
          >
            {t('campos.sacarElArchivo')}
          </Boton>
        )}
      </div>

      {campo.ayuda === null ? null : (
        <span className="text-apoyo text-texto-tenue">{campo.ayuda}</span>
      )}
      {error === null ? null : (
        <span role="alert" className="text-apoyo text-mal">
          {error}
        </span>
      )}
    </div>
  );
}
